"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import Text from "@afa-shared/afa-components/dist/Text";
import { ContentLink } from "@components/Link/ContentLink";
import Message from "@components/Message/Message";
import PaginationBlock from "@components/Pagination";
import Grid from "@components/client/Grid";
import {
  getFilteredItems,
  getPageBreadCrumbs,
  getRichText,
} from "@containers/InsuranceHandbookSearchPage/utils/sok-i-handbokenHelper";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { Suspense } from "react";
import { useEffect, useRef, useState } from "react";

import { FactsStructureSearchItem } from "types/FactsStructureSearchItem";

import insuranceSearchStyles from "../InsuranceSearch.module.css";
import { insuranceSearchResultClickTracker } from "../utils/insuranceSearchTracker";
import { SearchFeedback } from "./SearchFeedback";

const MAX_ITEMS_PER_PAGE = 9;

export default function Results({ factItems }: { factItems: any }) {
  const [queryValue] = useQueryState("query", parseAsString.withDefault(""));
  const [pageValue, setPageValue] = useQueryState(
    "page",
    parseAsInteger.withDefault(1).withOptions({ history: "replace" }),
  );
  const [pagedResults, setPagedResults] = useState<FactsStructureSearchItem[]>([]);
  const [resultsAmount, setResultsAmount] = useState<number>(0);
  const totalItems = factItems?.length || 0;
  const numberOfResultItems = Math.min(totalItems, MAX_ITEMS_PER_PAGE);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const filteredResults = getFilteredItems(queryValue, factItems);
    // Paginate results
    const startIndex = (pageValue - 1) * MAX_ITEMS_PER_PAGE;
    const endIndex = startIndex + MAX_ITEMS_PER_PAGE;
    const paginatedResults = filteredResults.slice(startIndex, endIndex);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPagedResults(paginatedResults || []);

    setResultsAmount(filteredResults?.length || 0);
  }, [queryValue, pageValue, factItems]);

  useEffect(() => {
    // Reset to first page when query changes
    setPageValue(1);
  }, [queryValue, setPageValue]);

  if (!queryValue) {
    return null;
  }

  const showResults = resultsAmount && !!queryValue;
  const totalPages = Math.ceil(resultsAmount / MAX_ITEMS_PER_PAGE);
  const showPagination = totalPages > 1;

  if (!showResults) {
    return (
      <>
        <Message
          type="info"
          heading="Din sökning gav inga träffar"
          headingVariant="h2"
          children={"Kontrollera stavningen eller prova med en synonym."}
        />
        <SearchFeedback totalItems={resultsAmount} />
      </>
    );
  }

  return (
    <>
      <Heading className={insuranceSearchStyles.heading} variant="h2">
        {resultsAmount + (resultsAmount === 1 ? ` träff` : ` träffar`)}
      </Heading>

      <ul className={insuranceSearchStyles.list}>
        {pagedResults?.map((item: FactsStructureSearchItem, index: number) => (
          <li key={index} className={insuranceSearchStyles.listItem}>
            <ContentLink
              href={item?.url}
              noTextDecoration={true}
              onClick={() =>
                insuranceSearchResultClickTracker(queryValue ?? "", totalItems, item?.heading ?? "")
              }
              noTracking={true}
              contentText={item?.heading}
            >
              <div className={insuranceSearchStyles.card}>
                <Grid alignItems="flex-start">
                  <Heading className={insuranceSearchStyles.cardHeading} variant="h3">
                    {item?.heading}
                  </Heading>
                  {(item?.preamble || item?.mainbody?.json) && (
                    <Text
                      variant="paragraph"
                      children={item?.preamble ? item?.preamble : getRichText(item?.mainbody?.json)}
                    />
                  )}
                  <Grid>{getPageBreadCrumbs(item, factItems)}</Grid>
                </Grid>
              </div>
            </ContentLink>
          </li>
        ))}
      </ul>

      {showPagination && (
        <Suspense fallback={null}>
          <PaginationBlock
            totalItems={resultsAmount}
            itemsPerPage={numberOfResultItems}
            searchParameters={{
              query: queryValue || undefined,
              page: pageValue,
            }}
            navbarLabel={"Paginering för sökträffar"}
            listRef={listRef}
          />
        </Suspense>
      )}
      <SearchFeedback totalItems={resultsAmount} />
    </>
  );
}
